import { FC, useCallback, useMemo, useEffect } from 'react'
import styles from 'components/features/socialFeed/FeedSearchForm/FeedSearchForm.styl'
import { ReduxFormProps } from 'interfaces/component'
import { Field, reduxForm, change } from 'redux-form'
import AutosuggestWrapped, { AutosuggestWrappedOwnProps } from 'components/atoms/inputs/AutosuggestWrapped'
import { FORM_TAG_SEARCH } from 'constants/forms'
import { getFeedTagsActionCreator } from 'actions/feedActions'
import { useStoreDispatch, useStoreSelector } from 'helpers/reactReduxHelpers'
import { TagsOrdering, FeedTagName, TagSearchOption } from 'interfaces/feed'
import { createTagsByOrderingSelector, createFeedSearchFormValuesSelector } from 'selectors/feedSelectors'
import { ContentRenderer } from 'components/atoms/inputs/Autosuggest'
import { createPluralFormatter } from 'helpers/formatHelpers'
import { FeedSearchFormValues } from 'interfaces/form'
import useRouter from 'use-react-router'
import { getAnchorUrl } from 'helpers/pageHelpers'
import { Anchor } from 'interfaces/navigation'
import { activateAnchorActionCreator } from 'actions/navigationActions'
import { urlSearchParamsToGetQueryParams } from 'helpers/observableAjaxHelpers'
import { FEED_SUGGESTS_LIMIT } from 'constants/feed'

const SEARCH_ORDERING: TagsOrdering = '-trend'
const getPluralPosts = createPluralFormatter('post', 'posts')

type FeedSearchFormProps =
  & ReduxFormProps<FeedSearchFormValues>
  & {

  }

const FeedSearchFormComponent: FC<FeedSearchFormProps> = (props) => {
  const {
    form,
  } = props

  const isTagSearchForm = form === FORM_TAG_SEARCH

  const noResultsChild = useMemo(() => {
    const copy = `${ isTagSearchForm ? 'Tag' : 'Member' } not found`
    return (
      <div className={ styles.noResults }>
        { copy }
      </div>
    )
  }, [isTagSearchForm])

  const tagsByTrendSelector = useMemo(() =>
    createTagsByOrderingSelector('-trend', FEED_SUGGESTS_LIMIT, form)
  , [form])

  const formValuesSelector = useMemo(
    () => createFeedSearchFormValuesSelector(form),
    [form]
  )

  const requestWithDetails = useStoreSelector(tagsByTrendSelector)
  const searchValue = useStoreSelector((state) => {
    return formValuesSelector(state).searchShadow
  })

  const { history } = useRouter()
  const dispatch = useStoreDispatch()
  useEffect(() => {
    if (searchValue) {
      dispatch(getFeedTagsActionCreator.request({
        ordering: SEARCH_ORDERING,
        search: searchValue,
        limit: FEED_SUGGESTS_LIMIT,
      }))
    }
  }, [dispatch, searchValue])


  const handleRequestSuggestions = useCallback<(query: string) => void>((query) => {
    if (!query.length) {
      return
    }

    if (form) {
      dispatch(change(form, 'searchShadow', query))
    }
  }, [dispatch, form])

  const handleSearchChange = useCallback<(option: TagSearchOption | null) => void>((option) => {
    if (!option) {
      return
    }

    const anchorObj: Anchor = { anchor: 'feedtag', id: option.value }
    const toUrl = getAnchorUrl(anchorObj)
    if (!toUrl) {
      return
    }

    history.push(toUrl)
    dispatch(activateAnchorActionCreator(anchorObj))
  }, [dispatch, history])

  const contentRenderer = useMemo<ContentRenderer<FeedTagName>>(() => {
    return (contentObj) => {
      const {
        options: items,
        noResults,
        query,
        renderContentWrapper,
        renderOptionWrapper,
      } = contentObj

      if (!query) {
        return null
      }

      if (!requestWithDetails || !requestWithDetails.payload) {
        return null
      }

      const searchObj = urlSearchParamsToGetQueryParams(new URLSearchParams(requestWithDetails.search))

      if (searchObj.search !== query) {
        return null
      }

      const { payload } = requestWithDetails

      const contentChild = noResults
        ? noResultsChild
        : renderContentWrapper(items.map((item, idx) => {
          const tag = payload.find(({ name }) => name === item.value)
          if (!tag) {
            return null
          }

          const itemChild = (
            <div className={ styles.item }>
              #{ tag.name }
              { !!tag.posts_count && (
                <span className={ styles.count }>
                  { tag.posts_count } { getPluralPosts(tag.posts_count) }
                </span>
              )}
            </div>
          )

          return renderOptionWrapper(item, idx, itemChild)
        }))

      return contentChild
    }
  }, [noResultsChild, requestWithDetails])


  const options = requestWithDetails
    ? requestWithDetails.payload.map((tag) => ({ label: tag.name, value: tag.name }))
    : []

  const autosuggestProps: AutosuggestWrappedOwnProps<FeedTagName> = {
    options,
    label: isTagSearchForm ? 'Search tags' : 'Search people',
    isLoading: Boolean(requestWithDetails && requestWithDetails.isLoading),
    qaType: 'search',
    qaMarker: isTagSearchForm ? 'tag-search' : 'user-search',
    showIcon: true,
    lastSentQuery: searchValue || '',
    onRequestSuggestions: handleRequestSuggestions,
    contentRenderer: contentRenderer,
    requestOptimization: false,
    isAlwaysBelowInput: true,
    autoComplete: 'off',
    showLabelAsLink: true,
    rounded: true,
  }

  return (
    <form onSubmit={ () => { /* do nothing */ } }>
      <Field
        component={ AutosuggestWrapped }
        name='search'
        onChange={ handleSearchChange }
        props={ autosuggestProps }
      />
    </form>
  )
}

export const FeedSearchForm = reduxForm({ initialValues: { searchShadow: '' }})(FeedSearchFormComponent)
